import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { PaginatorModule } from 'src/app/components/elementi-strutturali/paginator/paginator.module';
import { ApiResponse, Document, Event, Information, News, Service } from 'src/app/models/searchresult.model';
import { CompactCardComponent } from '../compact-card/compact-card.component';

type AcceptedType = News | Service | Document | Event | Information;

interface PaginationParams {
  identifier: string;
  currentPage: number;
  displayedItems: number;
}

@Component({
  selector: 'app-horizontal-band',
  standalone: true,
  imports: [CommonModule, PaginatorModule, RouterModule, CompactCardComponent],
  templateUrl: './horizontal-band.component.html',
  styleUrls: ['./horizontal-band.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HorizontalBandComponent<T extends AcceptedType> implements OnInit {
  @Input() apiMethod!: (params: Partial<PaginationParams>) => Observable<ApiResponse<T>>;
  @Input() identifier!: string;
  @Input() headingText: string = '';
  @Input() showAll: boolean = true;
  @Input() showAllText: string = "Mostra tutti";
  @Input() showAllRedirect: string = "";
  @Input() color?: string;
  @Input() firstElement?: boolean = false;
  isLoading: boolean = true;
  counter: number = 0


  @Output() noElements = new EventEmitter<string>();

  displayedItems = 6;

  private readonly currentPageSubject = new BehaviorSubject<number>(0);
  currentPage$ = this.currentPageSubject.asObservable().pipe(distinctUntilChanged());

  responseElements: T[] = [];
  private totalCount: number | null = null;

  constructor(private cdr: ChangeDetectorRef) { }

  data$: Observable<ApiResponse<T>> = this.currentPage$.pipe(
    debounceTime(10), // Prevent duplicate rapid emissions
    switchMap((page: number) => this.apiMethod({
      identifier: this.identifier,
      currentPage: page,
      displayedItems: this.displayedItems
    })),
    tap(response => {
      if (this.counter === 0) {
        this.firstElement = true
        this.counter = 1
      }
      this.responseElements = response.data

      if (this.totalCount === null) {
        this.totalCount = response.paginationInfo?.count || 0;
      }
      this.status.totalPage = Math.ceil((this.totalCount ?? 0) / this.displayedItems);
      this.cdr.markForCheck(); // Ensure UI update
      this.isLoading = false;
    }),
    catchError(err => {
      console.error('Error fetching data:', err);
      return EMPTY;
    })
  );

  ngOnInit(): void {
    this.data$.subscribe(); // Trigger API call and handle side effects
  }

  onPageChange(pageClicked: number) {
    this.isLoading = true;
    const newPage = pageClicked - 1; // Convert to 0-based
    if (newPage !== this.currentPageSubject.getValue()) {
      this.currentPageSubject.next(newPage);
      this.status.currentPage = newPage; // Synchronize status.currentPage
    }
    this.isLoading = false;
  }

  get sectionId(): string {
    return 'section-' + this.headingText
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9\-]/g, '');
  }

  status = {
    currentPage: 0,
    totalPage: 0,
    displayedItems: this.displayedItems
  };
}
